import React, { useRef, useState } from "react";
import { Offcanvas } from "react-bootstrap";

interface CustomOffcanvasProps {
  show: boolean;
  onHide: () => void;
  title?: string;
  children?: React.ReactNode;
  customStyles?: React.CSSProperties;
  imageUrl?: string;
}

const CustomOffcanvas: React.FC<CustomOffcanvasProps> = ({
  show,
  onHide,
  title = "Offcanvas",
  children,
  customStyles = {},
  imageUrl,
}) => {
  const startY = useRef<number | null>(null);
  const [dragY, setDragY] = useState(0);

  const handleStart = (y: number) => {
    startY.current = y;
  };

  const handleMove = (y: number) => {
    if (startY.current !== null) {
      const deltaY = y - startY.current;
      if (deltaY > 0) setDragY(deltaY);
    }
  };

  const handleEnd = () => {
    if (dragY > 120) {
      onHide();
    }
    setDragY(0);
    startY.current = null;
  };

  const handleTouchStart = (e: React.TouchEvent) => handleStart(e.touches[0].clientY);
  const handleTouchMove = (e: React.TouchEvent) => handleMove(e.touches[0].clientY);
  const handleTouchEnd = () => handleEnd();

  const handleMouseDown = (e: React.MouseEvent) => {
    e.preventDefault();
    handleStart(e.clientY);
    const onMouseMove = (ev: MouseEvent) => handleMove(ev.clientY);
    const onMouseUp = () => {
      handleEnd();
      window.removeEventListener("mousemove", onMouseMove);
      window.removeEventListener("mouseup", onMouseUp);
    };
    window.addEventListener("mousemove", onMouseMove);
    window.addEventListener("mouseup", onMouseUp);
  };

  const defaultStyle: React.CSSProperties = {
    width: "100%",
    height: "98vh",
    borderTopLeftRadius: "25px",
    borderTopRightRadius: "25px",
    position: "fixed",
    left: "50%",
    bottom: 0,
    transform: `translate(-50%, ${dragY}px)`,
    transition: dragY > 0 ? "none" : "transform 0.3s ease-in-out",
    backdropFilter: "blur(4px)",
    background: "#F6F4F2",
    boxShadow: "0px -4px 20px rgba(0, 0, 0, 0.1)",
    overflowY: "auto",
    animation: show ? "slideUp 0.5s ease-in-out" : "slideDown 0.5s ease-in-out",
    touchAction: "none",
  };

  const contentWrapperStyle: React.CSSProperties = {
      position: "relative",
      zIndex: 1,
      marginTop: "250px",
      background: "#F6F4F2",
      borderTopLeftRadius: "30px",
      borderTopRightRadius: "30px",
      padding: "30px 20px",
      boxShadow: "0px -5px 15px rgba(0, 0, 0, 0.05)",
  };

  const pullIndicatorStyle: React.CSSProperties = {
    width: "40px",
    height: "6px",
    backgroundColor: "#c39d63",
    borderRadius: "3px",
    margin: "12px auto 8px",
    animation: "pulse 1.5s ease-in-out infinite",
    cursor: "grab",
  };

  const pulseKeyframes = `
    @keyframes pulse {
      0% { transform: scaleX(1); opacity: 1; }
      50% { transform: scaleX(1.3); opacity: 0.7; }
      100% { transform: scaleX(1); opacity: 1; }
    }
  `;

  return (
    <>
      <style>{pulseKeyframes}</style>

      <Offcanvas
        show={show}
        onHide={onHide}
        placement="bottom"
        style={{ ...defaultStyle, ...customStyles }}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
      >
        <div
          style={pullIndicatorStyle}
          onTouchStart={handleTouchStart}
          onTouchMove={handleTouchMove}
          onTouchEnd={handleTouchEnd}
          onMouseDown={handleMouseDown}
        />
        <div style={{margin: "0 20px 0 20px"}}>
          <Offcanvas.Header style={{ height: 95 }} closeButton>
              <Offcanvas.Title style={{ fontSize: "1.8rem", fontFamily: "Avenir Next Codensed Regular" }}>{title}</Offcanvas.Title>
          </Offcanvas.Header>
        </div>
        <Offcanvas.Body style={{ padding: 0 }}>
          {imageUrl && (
            <div
              style={{
                position: "absolute",
                top: 120,
                left: 0,
                width: "100%",
                height: "350px",
                zIndex: -1,
                backgroundImage: `url(${imageUrl})`,
                backgroundSize: "cover",
                backgroundPosition: "center",
              }}
            />
          )}

          <div style={contentWrapperStyle}>
            {children}
          </div>
        </Offcanvas.Body>
      </Offcanvas>
    </>
  );
};

export default CustomOffcanvas;
